/** @module csi/table/MemoryTable */
define([
	"dojo/_base/declare",
	"underscore",
	"csi/html",
	"csi/table"
], function(declare, _, html, csiTable) {

	/**
	 * Memory structure holding DOM table cells keyed by id
	 */

	const CSS_FROZEN_LEFT = "csi-table-frozen-left";
	const CSS_FROZEN_RIGHT = "csi-table-frozen-right";
	const CSS_FROZEN_OUT = "csi-table-frozen-out";
	const CSS_FROZEN_WRAP = "csi-table-frozen-wrap";
	const CSS_FROZEN_END = "csi-table-frozen-end";

	// for now there is just one CSS class to omit from wrapping left/right cells; if others, then need an array and adjust wrap logic below
	const NO_WRAP_CLASS = html.CSS_PASTE_FOCUS;

	html.registerDataAttribute("DATA_ROW_ID", "row-id");

	csiTable.registerCellClass(CSS_FROZEN_LEFT);
	csiTable.registerCellClass(CSS_FROZEN_RIGHT);
	csiTable.registerCellClass(CSS_FROZEN_OUT);

	/**
	 * Add row's child cells to array where cells could possibly span multiple columns (colspan), but no missing cells due to rowspan
	 * @param {DOMNode} row
	 * @param {DOMNode[]} cells
	 */
	function _addCells(row, cells) {
		var cell = row.firstChild;
		var i = -1;
		var j;
		var nSpan;

		while (cell) {
			cells[++i] = cell;
			nSpan = cell.colSpan;

			for (j = 2; j <= nSpan; ++j) {
				cells[++i] = cell;
			}

			cell = cell.nextSibling;
		}

		console.assert(i == cells.length - 1);
	}

	/**
	 * Add row's child cells to array where cells could possibly span multiple columns, and there could be missing cells due to rowspan
	 * @param {DOMNode} baseRow
	 * @param {DOMNode} row
	 * @param {DOMNode[]} cells
	 */
	function _addMergedCells(baseRow, row, cells) {
		var baseCell = baseRow.firstChild;
		var cell = row.firstChild;
		var i = -1;
		var j;
		var nSpan;

		while (baseCell) {
			if (baseCell.rowSpan === 1) {
				// cell exists on row, so add to cells
				cells[++i] = cell;

				// if cell has colspan, then add same cell that many times
				nSpan = cell.colSpan;

				if (nSpan > 1) {
					for (j = 2; j <= nSpan; ++j) {
						cells[++i] = cell;
					}

					cell = cell.nextSibling;
				} else {
					// if base cell has colspan, then need to iterate over next cells that many times;
					// for now, only one can have colspan
					nSpan = baseCell.colSpan;
					cell = cell.nextSibling;

					for (j = 2; j <= nSpan; ++j) {
						cells[++i] = cell;
						cell = cell.nextSibling;
					}
				}
			} else {
				// cell only exists on base row, so add null for merged row
				cells[++i] = null;
			}

			baseCell = baseCell.nextSibling;
		}

		console.assert(i == cells.length - 1);
	}

	/**
	 * Return map id for row
	 * @param {DOMNode} row
	 * @return {string}
	 */
	function _getMapId(row) {
		return _getRowId(row) || _getResultId(row);
	}

	/**
	 * Return result id for row
	 * @param {DOMNode} row
	 * @return {string|null}
	 */
	function _getResultId(row) {
		return html.getDataAttribute(row, html.DATA_RESULT);
	}

	/**
	 * Return row id for header or merged rows
	 * @param {DOMNode} row
	 * @return {string|null}
	 */
	function _getRowId(row) {
		return html.getDataAttribute(row, html.DATA_ROW_ID);
	}

	/**
	 * Show cell because it is now visible in frozen view
	 * @param {DOMNode} cell
	 */
	function _scrollIn(cell) {
		if (cell) {
			const jCell = $(cell);

			if (jCell.hasClass(CSS_FROZEN_OUT)) {
				jCell.removeClass(CSS_FROZEN_OUT);
			} else {
				++cell.colSpan;
			}
		}
	}

	/**
	 * Hide cell because it is no longer in range of frozen view
	 * @param {DOMNode} cell
	 */
	function _scrollOut(cell) {
		if (cell) {
			if (cell.colSpan === 1) {
				$(cell).addClass(CSS_FROZEN_OUT);
			} else {
				--cell.colSpan;
			}
		}
	}

	/**
	 * Wrap cell contents with div so that it can be shown partially
	 * @param {DOMNode} cell 
	 */
	function _wrap(cell) {
		if (!cell || (cell.colSpan > 1)) {
			return;
		}

		let domNode = cell.firstChild;

		if (domNode && !$(domNode).hasClass(CSS_FROZEN_WRAP)) {
			const div = html.create("div", {
				className: CSS_FROZEN_WRAP
			}, cell, "first");

			// for now there is just one class to omit from wrapping (could be anything positioned absolutely within cell)
			let next;

			while (domNode) {
				next = domNode.nextSibling;

				if (!$(domNode).hasClass(NO_WRAP_CLASS)) {
					div.appendChild(domNode);
				}

				domNode = next;
			}
		}
	}

	return declare(null, {
		/** @property {number} n - total number of columns in table, including extra column */
		n: 0,

		/** @property {number} nLast - number of data columns in table, which is same index as column that is displayed after right frozen border */
		nLast: 0,

		/**
		 * @property {Object<string,DOMNode[]>} _map - map of row id to array of cells;
		 * where row spans exist (such as for matrices), there could be null entries in array;
		 * where col spans exist, the same cell can appear multiple times consecutively
		 */
		_map: null,

		constructor: function(numberOfColumns, numberOfDataColumns) {
			this.n = numberOfColumns;
			this.nLast = numberOfDataColumns;
			this._map = {};
		},

		/**
		 * Add row to map
		 * @param {DOMNode} row
		 * @param {DOMNode} [rowMergedWith] - row that given row is merged with (if any)
		 */
		add: function(row, rowMergedWith) {
			const bMerged = !!rowMergedWith || csiTable.isMergedRow(row);
			const resultId = _getResultId(row);
			let id = _getRowId(row);

			if (!id) {
				id = resultId;

				// rows in table header will not have a result id, and merged rows (for matrix) have a result id that is
				// the same as their base row, so both will need a new and unique row id
				if (!id || bMerged) {
					id = _.uniqueId("TR");
					html.setDataAttribute(row, html.DATA_ROW_ID, id);
				}
			}

			const cells = this._map[id] = Array(this.n);

			if (bMerged) {
				// process row that will be missing cells
				const baseCells = this._map[resultId || _getRowId(rowMergedWith)];
				const baseRow = baseCells[0].parentNode;
				_addMergedCells(baseRow, row, cells);
			} else {
				// process row that may include colspan
				_addCells(row, cells);
			}
		},

		/**
		 * Add CSS class to cell at given index
		 * @param {DOMNode} row
		 * @param {number} index
		 * @param {string} css
		 * @param {number} [onlyIfNotIndex] - if specified, then only apply class if cell is different from that at this index
		 */
		addCellClass: function(row, index, css, onlyIfNotIndex) {
			const cells = this._getCells(row);
			const cell = cells[index];

			if (cell && ((onlyIfNotIndex == null) || (cells[onlyIfNotIndex] != cell))) {
				$(cell).addClass(css);
			}
		},

		/**
		 * Return true if cell originally spans multiple columns in the table
		 * @param {DOMNode} cell 
		 * @return {boolean}
		 */
		cellSpansMultipleColumns: function(cell) {
			return this._spansColumns(cell, this.getCellIndex(cell));
		},

		/**
		 * Return row containing cells specified by frozen column, left and right borders of scrolling columns, and optional actions
		 * @param {DOMNode} row
		 * @param {number} nFrozen
		 * @param {number} nLeft
		 * @param {number} nRight
		 */
		freezeRow: function(row, nFrozen, nLeft, nRight) {
			const cells = this._getCells(row);
			const leftCell = cells[nLeft];
			const rightCell = cells[nRight];

			this._setTableFrozenColumnBorder(row, nFrozen, true);

			if (leftCell && !this._spansColumns(leftCell, nLeft, nFrozen)) {
				$(leftCell).addClass(CSS_FROZEN_LEFT);
				_wrap(leftCell);
			}

			if (rightCell && !this._spansColumns(rightCell, nRight, this.nLast)) {
				$(rightCell).addClass(CSS_FROZEN_RIGHT);
				_wrap(rightCell);
			}

			for (let i = nFrozen + 1; i < nLeft; ++i) {
				_scrollOut(cells[i]);
			}

			for (let i = nRight + 1; i < this.nLast; ++i) {
				_scrollOut(cells[i]);
			}
		},

		/**
		 * Return table cell at given index for row with given id
		 * @param {DOMNode} row
		 * @param {number} index
		 * @param {number} testIndexForSpan - if specified, then return null if cell is same as that at this other index
		 * @return {DOMNode}
		 */
		getCellAtIndex: function(row, index, testIndexForSpan) {
			const cells = this._getCells(row);
			const cell = cells[index];

			if ((testIndexForSpan >= 0) && (cell === cells[testIndexForSpan])) {
				return null;
			}

			return cell;
		},

		/**
		 * Return index for given table cell
		 * @param {DOMNode} cell
		 * @return {number}
		 */
		getCellIndex: function(cell) {
			return _.indexOf(this._getCells(cell.parentNode), cell);
		},

		/**
		 * Return max index for given table cell if it spans multiple columns
		 * @param {DOMNode} cell
		 * @return {number}
		 */
		getCellMaxIndex: function(cell) {
			const cells = this._getCells(cell.parentNode);
			let index = this.getCellIndex(cell);

			while (cells[++index] === cell);

			return index - 1;
		},

		/**
		 * Remove row from map
		 * @param {DOMNode} row
		 */
		remove: function(row) {
			const id = _getMapId(row);
			delete this._map[id];
		},

		/**
		 * Remove CSS class from cell at given index
		 * @param {DOMNode} row
		 * @param {number} index
		 * @param {string} css
		 */
		removeCellClass: function(row, index, css) {
			const cell = this.getCellAtIndex(row, index);
			if (cell) {
				$(cell).removeClass(css);
			}
		},

		/**
		 * Move frozen borders of previously frozen row to new columns
		 * @param {DOMNode} row
		 * @param {number} nOldFrozen
		 * @param {number} nFrozen
		 * @param {number} nOldLeft
		 * @param {number} nLeft
		 * @param {number} nOldRight
		 * @param {number} nRight
		 * @param {Object<number,number>} oldColumnMap
		 * @param {Object<number,number>} newColumnMap
		 */
		scrollRow: function(row, nOldFrozen, nFrozen, nOldLeft, nLeft, nOldRight, nRight, oldColumnMap, newColumnMap) {
			// update left and right border styling; due to colspan, it is possible that no changes are needed
			// (such as on action/group/divider rows); it is also possible that the left or right border does not change - just the other border
			const {nLast} = this;
			const cells = this._getCells(row);
			const oldLeftCell = cells[nOldLeft];
			const newLeftCell = cells[nLeft];
			const oldRightCell = cells[nOldRight];
			const newRightCell = cells[nRight];

			if (nOldFrozen != nFrozen) {
				this._setTableFrozenColumnBorder(row, nOldFrozen, false);
				this._setTableFrozenColumnBorder(row, nFrozen, true);
			}

			if (oldLeftCell !== newLeftCell) {
				$(oldLeftCell).removeClass(CSS_FROZEN_LEFT);
			}

			if (oldRightCell !== newRightCell) {
				$(oldRightCell).removeClass(CSS_FROZEN_RIGHT);
			}

			if (!this._spansColumns(newLeftCell, nLeft, nFrozen)) {
				$(newLeftCell).addClass(CSS_FROZEN_LEFT);
				_wrap(newLeftCell);
			}

			if (!this._spansColumns(newRightCell, nRight, nLast)) {
				$(newRightCell).addClass(CSS_FROZEN_RIGHT);
				_wrap(newRightCell);
			}

			// scroll cells in or out relative to previous state
			let x;
			let y;

			for (let i = 0; i < nLast; ++i) {
				x = oldColumnMap[i];
				y = newColumnMap[i];

				if (x && !y) {
					_scrollOut(cells[i]);
				} else if (!x && y) {
					_scrollIn(cells[i]);
				}
			}
		},

		/**
		 * Remove styling from frozen row
		 * @param {DOMNode} row
		 * @param {number} nFrozen
		 * @param {number} nLeft
		 * @param {number} nRight
		 */
		unfreezeRow: function(row, nFrozen, nLeft, nRight) {
			const cells = this._getCells(row);
			var i;

			$(cells[nLeft]).removeClass(CSS_FROZEN_LEFT);
			$(cells[nRight]).removeClass(CSS_FROZEN_RIGHT);

			// reset the border styling
			this._setTableFrozenColumnBorder(row, nFrozen, false);

			for (i = nFrozen + 1; i < nLeft; ++i) {
				_scrollIn(cells[i]);
			}

			for (i = nRight + 1; i < this.nLast; ++i) {
				_scrollIn(cells[i]);
			}
		},

		/**
		 * Return cells array for row
		 * @param {DOMNode} row
		 * @return {DOMNode[]}
		 */
		_getCells: function(row) {
			const id = _getMapId(row);
			const cells = this._map[id];
			console.assert(!!cells);
			return cells;
		},

		/**
		 * Set the visibility of the frozen column border, including both the table head and table body
		 * @param {DOMNode} row
		 * @param {number} nFrozen
		 * @param {boolean} visible
		 */
		_setTableFrozenColumnBorder: function(row, nFrozen, visible) {
			const cells = this._getCells(row);

			if (!cells) {
				// in the case of table foot row or group row
				return;
			}

			const freezeEndCell = cells[nFrozen];

			if (freezeEndCell && !this._spansColumns(freezeEndCell, nFrozen, nFrozen + 1)) {
				// in the case of merged row, freezeEndCell might be null
				freezeEndCell.classList.toggle(CSS_FROZEN_END, visible);
			}
		},

		/**
		 * Return true if given cell is same as cell on either side; we cannot just test for colSpan
		 * since that could be reduced when scrolling
		 * @param {DOMNode} cell 
		 * @param {number} index 
		 * @param {number} [testIndex] - if specified, see if cell at two indices match
		 * @return {boolean}
		 */
		_spansColumns: function(cell, index, testIndex) {
			if (!cell) {
				return false;
			}

			const row = cell.parentNode;
			const cells = this._getCells(row);

			if (testIndex != null) {
				return cell === cells[testIndex];
			}

			return (cell === cells[index - 1]) || (cell === cells[index + 1]);
		}
	});
});